import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import {
    SettingsPanel,
    SettingsRow,
    SettingsSection,
} from '@apps/web/components/settings/settings_primitives';
import { SmsCancellationReplyCard } from '@apps/web/components/settings/sms_cancellation_reply_card';
import { getTemplateVariablePreviewValuesForOrganization } from '@apps/web/components/shared/messages/template_variable_metadata';
import {
    AvailableVariables,
    VariableTextEditor,
} from '@apps/web/components/shared/messages/variable_text_editor';
import { Button, type ButtonStatus } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { Input } from '@apps/web/components/ui/input';
import { Switch } from '@apps/web/components/ui/switch';
import {
    SettingsPageSection,
    SettingsPageSections,
    SettingsPageShell,
    useSettingsGates,
} from '@apps/web/features/settings';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { getTemplateVariablesForFlow } from '@apps/web/lib/template_variables';
import {
    useCompletionTemplates,
    useOrganization,
    useTemplateVariableManifest,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute('/_app/settings/cancellations')({
    component: CancellationsPage,
});

const DEFAULT_COMPLETION_CANCELLED_TEMPLATE =
    'Hi {caregiver_first_name}, the following shifts have been cancelled: {shift_list}';

function CancellationsPage() {
    const { organizationId, gates } = useSettingsGates();
    if (!gates.canManageOrgSettings) {
        return <Navigate to="/settings" replace={true} />;
    }
    if (organizationId === null) {
        return null;
    }

    return (
        <SettingsPageShell
            title="Cancellations"
            description="What happens when a caregiver calls out of a shift, and the messages Phoebe sends when shifts are cancelled."
        >
            <SettingsPageSections>
                <CaregiverCalloutsSection organizationId={organizationId} />
                <AgencyCancellationNoticeSection
                    organizationId={organizationId}
                />
            </SettingsPageSections>
        </SettingsPageShell>
    );
}

/** Blur-to-save hours input rendered inline inside the policy sentence. */
function ReviewWindowHoursInput({
    savedHours,
    disabled,
    onSave,
}: {
    readonly savedHours: number;
    readonly disabled: boolean;
    /** Resolves false when the save failed so the draft can be reset. */
    readonly onSave: (hours: number) => Promise<boolean>;
}) {
    const [draft, setDraft] = useState(String(savedHours));
    useEffect(() => {
        setDraft(String(savedHours));
    }, [savedHours]);

    const save = async () => {
        const normalized = draft.trim();
        const parsed = Number(normalized);
        if (normalized === '' || !Number.isFinite(parsed) || parsed < 0) {
            toast.error('Enter a non-negative number of hours', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
            setDraft(String(savedHours));
            return;
        }
        if (parsed === savedHours) {
            return;
        }
        const saved = await onSave(parsed);
        if (!saved) {
            // Otherwise the box keeps showing a number that was never stored.
            setDraft(String(savedHours));
        }
    };

    return (
        <Input
            id="callout-review-window"
            aria-label="Review window (hours)"
            type="number"
            min={0}
            step="any"
            className="inline-block h-8 w-20 text-center"
            value={draft}
            disabled={disabled}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => {
                void save();
            }}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    event.currentTarget.blur();
                }
            }}
        />
    );
}

/**
 * Caregiver call-outs as one policy: a master switch, the channels caregivers
 * can use, and a single review window shared by both channels. The two
 * channel enable flags and the two threshold columns stay independent in the
 * database; this surface writes them together.
 */
function CaregiverCalloutsSection({
    organizationId,
}: {
    readonly organizationId: string;
}) {
    const { data: organization, mutate: mutateOrganization } = useOrganization(
        organizationId,
        { reason: 'settings-cancellations-callouts' },
    );
    const [isUpdating, setIsUpdating] = useState(false);

    const generalSettings = organization?.general_settings;
    const smsEnabled =
        generalSettings?.sms_shift_cancellation_user_enabled ?? false;
    const voiceEnabled =
        generalSettings?.inbound_voice_shift_cancellation_enabled ?? false;
    const policyEnabled = smsEnabled || voiceEnabled;
    // The channels share one review window on this surface; when legacy data
    // has them apart, the SMS value is shown and the next save realigns both.
    const reviewWindowHours =
        generalSettings?.sms_shift_cancellation_transfer_threshold_hours ?? 24;

    const alwaysTransferPhoneCallouts =
        generalSettings?.inbound_voice_shift_cancellation_always_transfer ??
        false;

    const putGeneralSettings = async (
        patch: Partial<{
            readonly sms_shift_cancellation_user_enabled: boolean;
            readonly inbound_voice_shift_cancellation_enabled: boolean;
            readonly sms_shift_cancellation_transfer_threshold_hours: number;
            readonly inbound_voice_shift_cancellation_transfer_threshold_hours: number;
            readonly inbound_voice_shift_cancellation_always_transfer: boolean;
        }>,
        successMessage: string,
    ): Promise<boolean> => {
        setIsUpdating(true);
        try {
            await getApiClient().put({
                url: '/organizations/{organization_id}/general-settings',
                pathParams: { organization_id: organizationId },
                body: patch,
            });
            await mutateOrganization();
            toast.success(successMessage, {
                persistNotification: true,
                notificationType: 'settings_update',
            });
            return true;
        } catch {
            toast.error('Failed to update call-out settings', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
            return false;
        } finally {
            setIsUpdating(false);
        }
    };

    const handlePolicyToggle = (enabled: boolean) =>
        putGeneralSettings(
            {
                sms_shift_cancellation_user_enabled: enabled,
                inbound_voice_shift_cancellation_enabled: enabled,
            },
            enabled
                ? 'Caregivers can now cancel shifts through Phoebe'
                : 'Call-outs through Phoebe turned off',
        );

    const handleChannelToggle = (channel: 'sms' | 'voice', enabled: boolean) =>
        putGeneralSettings(
            channel === 'sms'
                ? { sms_shift_cancellation_user_enabled: enabled }
                : { inbound_voice_shift_cancellation_enabled: enabled },
            'Call-out channels updated',
        );

    return (
        <SettingsPageSection
            title="Caregiver call-outs"
            description="Let caregivers cancel upcoming shifts through Phoebe, and choose when a scheduler reviews the request first."
        >
            <SettingsSection>
                <SettingsRow
                    htmlFor="callouts-enabled"
                    title="Let caregivers cancel shifts through Phoebe"
                    tooltip="When off, Phoebe never records a cancellation on its own — caregivers who try are pointed to the office instead."
                    control={
                        <Switch
                            id="callouts-enabled"
                            checked={policyEnabled}
                            disabled={isUpdating}
                            onCheckedChange={handlePolicyToggle}
                        />
                    }
                />
                {policyEnabled ? (
                    <Fragment>
                        <SettingsRow
                            title="Channels"
                            control={
                                <div className="flex flex-wrap gap-x-8 gap-y-2">
                                    <label className="flex cursor-pointer items-center gap-2 text-sm">
                                        <Checkbox
                                            checked={smsEnabled}
                                            disabled={isUpdating}
                                            onCheckedChange={(checked) =>
                                                handleChannelToggle(
                                                    'sms',
                                                    checked === true,
                                                )
                                            }
                                        />
                                        Text message
                                    </label>
                                    <label className="flex cursor-pointer items-center gap-2 text-sm">
                                        <Checkbox
                                            checked={voiceEnabled}
                                            disabled={isUpdating}
                                            onCheckedChange={(checked) =>
                                                handleChannelToggle(
                                                    'voice',
                                                    checked === true,
                                                )
                                            }
                                        />
                                        Phone call
                                    </label>
                                </div>
                            }
                        />
                        <div className="space-y-1.5 px-5 py-4">
                            <p className="text-foreground flex flex-wrap items-center gap-x-1.5 gap-y-2 text-[15px] leading-7 font-medium">
                                <span>If the shift starts within</span>
                                <ReviewWindowHoursInput
                                    savedHours={reviewWindowHours}
                                    disabled={isUpdating}
                                    onSave={(hours) =>
                                        putGeneralSettings(
                                            {
                                                sms_shift_cancellation_transfer_threshold_hours:
                                                    hours,
                                                inbound_voice_shift_cancellation_transfer_threshold_hours:
                                                    hours,
                                            },
                                            'Review window updated',
                                        )
                                    }
                                />
                                <span>
                                    hours, a scheduler reviews the request
                                    before it takes effect.
                                </span>
                            </p>
                            <p className="text-muted-foreground text-sm">
                                Requests further out are recorded automatically.
                            </p>
                        </div>
                        {voiceEnabled ? (
                            <SettingsRow
                                htmlFor="callouts-voice-always-transfer"
                                title="Connect phone call-outs to a scheduler"
                                tooltip="Phoebe still captures the call-out for post-call confirmation, then hands the live call to the office so a scheduler can talk it through."
                                control={
                                    <Switch
                                        id="callouts-voice-always-transfer"
                                        checked={alwaysTransferPhoneCallouts}
                                        disabled={isUpdating}
                                        onCheckedChange={(enabled) =>
                                            putGeneralSettings(
                                                {
                                                    inbound_voice_shift_cancellation_always_transfer:
                                                        enabled,
                                                },
                                                enabled
                                                    ? 'Phone call-outs will be connected to a scheduler'
                                                    : 'Phone call-outs will end with Phoebe',
                                            )
                                        }
                                    />
                                }
                            />
                        ) : null}
                    </Fragment>
                ) : (
                    <div className="px-5 py-4">
                        <SmsCancellationReplyCard
                            organizationId={organizationId}
                            reply={
                                organization?.shift_confirmation_settings
                                    ?.sms_shift_cancellation_disabled_reply ??
                                null
                            }
                            defaultReply={
                                organization?.shift_confirmation_settings
                                    ?.sms_shift_cancellation_disabled_reply_default ??
                                ''
                            }
                            isLoading={organization === undefined}
                            onSaved={async () => {
                                await mutateOrganization();
                            }}
                        />
                    </div>
                )}
            </SettingsSection>
            <p className="text-muted-foreground px-1 text-sm">
                Accepted call-outs are recorded on the shift, and what Phoebe
                does next is set under{' '}
                <Link to="/settings/phoebe" className="underline">
                    Phoebe
                </Link>
                . Phone call-outs can also vary by answer schedule on the{' '}
                <Link to="/settings/phone" className="underline">
                    Call handling
                </Link>{' '}
                page.
            </p>
        </SettingsPageSection>
    );
}

function AgencyCancellationNoticeSection({
    organizationId,
}: {
    readonly organizationId: string;
}) {
    const { data: templates, mutate: mutateTemplates } = useCompletionTemplates(
        organizationId,
        { reason: 'settings-cancellations-notice' },
    );
    const { data: organization } = useOrganization(organizationId, {
        reason: 'settings-cancellations-notice-organization',
    });
    const { data: variableManifest } = useTemplateVariableManifest(
        organizationId,
        { reason: 'settings-cancellations-notice-variables' },
    );
    // Completion notices share the completion SMS header variable flow, same
    // as the classic-settings Messages card.
    const templateVariables = useMemo(
        () =>
            getTemplateVariablesForFlow(
                variableManifest,
                'completion_sms_header',
            ),
        [variableManifest],
    );
    const savedTemplate = useMemo(
        () =>
            templates?.find((t) => t.type === 'completion_cancelled')
                ?.template ?? DEFAULT_COMPLETION_CANCELLED_TEMPLATE,
        [templates],
    );
    const [draft, setDraft] = useState(savedTemplate);
    const [saveStatus, setSaveStatus] = useState<ButtonStatus>('idle');
    const isSaving = saveStatus === 'pending';
    useEffect(() => {
        setDraft(savedTemplate);
    }, [savedTemplate]);

    const hasChanges = draft.trim() !== savedTemplate;

    const handleSave = useCallback(async () => {
        setSaveStatus('pending');
        try {
            await getApiClient().put({
                url: '/organizations/{organization_id}/templates',
                pathParams: { organization_id: organizationId },
                body: {
                    templates: [
                        {
                            type: 'completion_cancelled',
                            template: draft.trim(),
                        },
                    ],
                },
            });
            await mutateTemplates();
            setSaveStatus('success');
            toast.success('Cancellation notice saved', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
        } catch {
            setSaveStatus('error');
            toast.error('Failed to save cancellation notice', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
        }
    }, [organizationId, draft, mutateTemplates]);

    return (
        <SettingsPageSection
            title="When you cancel shifts"
            description="The text Phoebe sends caregivers when you cancel the shifts from an open-shift outreach."
        >
            <SettingsPanel>
                <div className="space-y-3">
                    <AvailableVariables
                        variables={templateVariables}
                        label="Drag these variables"
                    />
                    <VariableTextEditor
                        value={draft}
                        onChange={setDraft}
                        variables={templateVariables}
                        variablePreviewValues={getTemplateVariablePreviewValuesForOrganization(
                            organization,
                        )}
                        ariaLabel="Cancellation notice template"
                        placeholder={DEFAULT_COMPLETION_CANCELLED_TEMPLATE}
                    />
                    <div className="flex flex-wrap gap-2">
                        <Button
                            size="sm"
                            onClick={handleSave}
                            status={saveStatus}
                            disabled={!hasChanges}
                        >
                            {isSaving ? 'Saving…' : 'Save'}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDraft(savedTemplate)}
                            disabled={!hasChanges || isSaving}
                        >
                            Discard Changes
                        </Button>
                    </div>
                </div>
            </SettingsPanel>
        </SettingsPageSection>
    );
}
